import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, services, testimonials } from '../data/mock';

export const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-forest-50 to-transparent opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="inline-block px-4 py-2 bg-forest-100 text-forest-800 rounded-full text-sm font-medium mb-6">
                KFZ-Sachverständiger für Schäden und Bewertung
              </div>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                {companyInfo.slogan}
              </h1>
              <p className="text-lg lg:text-xl text-gray-600 mb-8">
                {companyInfo.description}. Wir erstellen professionelle Gutachten nach höchsten Standards.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/kontakt">
                  <Button size="lg" className="bg-forest-600 hover:bg-forest-700 text-white w-full sm:w-auto">
                    Jetzt Termin vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/leistungen">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-forest-600 text-forest-700 hover:bg-forest-50">
                    Unsere Leistungen
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/GutachterBild.jpeg"
                  alt="Professionelle Kfz-Begutachtung"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-forest-100 rounded-full flex items-center justify-center">
                    <Shield className="h-6 w-6 text-forest-700" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">100% Unabhängig</div>
                    <div className="text-sm text-gray-600">Objektive Bewertung</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Expertise Banner - Full Width */}
          <div className="mt-20 pt-12 border-t border-gray-200">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-r from-forest-50 to-forest-100 border-l-4 border-forest-600 p-6 rounded-r-xl shadow-md">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-forest-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-forest-900 mb-1">
                      Jahrelange Erfahrung als Kfz-Techniker-Meister
                    </h3>
                    <p className="text-sm text-forest-800">
                      Fundiertes technisches Fachwissen für präzise Schadensbeurteilung
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-forest-50 to-forest-100 border-l-4 border-forest-600 p-6 rounded-r-xl shadow-md">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-forest-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-forest-900 mb-1">
                      Schnelle Bearbeitung & Termintreue
                    </h3>
                    <p className="text-sm text-forest-800">
                      Gutachten schnellstmöglich erstellt - Terminvereinbarung oft am selben Tag
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-forest-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-forest-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-6 w-6 text-forest-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Objektiv & Unabhängig</h3>
                <p className="text-gray-600 text-sm">
                  Als neutrale Sachverständige sind wir keiner Partei verpflichtet. Ihre Interessen stehen im Mittelpunkt.
                </p>
              </CardContent>
            </Card>

            <Card className="border-forest-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-forest-100 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="h-6 w-6 text-forest-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Schnelle Bearbeitung</h3>
                <p className="text-gray-600 text-sm">
                  Ihr Gutachten erhalten Sie schnellstmöglich. Terminvereinbarung oft noch am selben Tag möglich.
                </p>
              </CardContent>
            </Card>

            <Card className="border-forest-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-forest-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-forest-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Professionelle Standards</h3>
                <p className="text-gray-600 text-sm">
                  Unsere Gutachten erfüllen alle erforderlichen Standards und werden von Versicherungen bundesweit akzeptiert.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Unsere Leistungen</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professionelle Gutachten für alle Bereiche – von Unfallschäden bis zur Oldtimer-Bewertung
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {services.slice(0, 6).map((service) => (
              <Card key={service.id} className="hover:shadow-lg transition-all hover:-translate-y-1">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/leistungen">
              <Button variant="outline" size="lg" className="border-forest-600 text-forest-700 hover:bg-forest-50">
                Alle Leistungen ansehen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Unsere Partner</h2>
            <p className="text-lg text-gray-600">
              Wir arbeiten mit führenden Branchenexperten zusammen
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            <div className="flex items-center justify-center p-6 bg-white rounded-xl hover:shadow-lg transition-shadow border border-gray-100 h-32">
              <img 
                src="https://customer-assets.emergentagent.com/job_fahrzeug-pruefung/artifacts/8493lydb_logo-autoixpert-min-2.png" 
                alt="autoiXpert - Gutachter Software"
                className="h-20 w-auto object-contain"
              />
            </div>

            <div className="flex items-center justify-center p-6 bg-white rounded-xl hover:shadow-lg transition-shadow border border-gray-100 h-32">
              <img 
                src="https://customer-assets.emergentagent.com/job_fahrzeug-pruefung/artifacts/qstv7tj2_dat.png" 
                alt="DAT - Fahrzeugbewertung"
                className="h-20 w-auto object-contain"
              />
            </div>

            <div className="flex items-center justify-center p-6 bg-white rounded-xl hover:shadow-lg transition-shadow border border-gray-100 h-32">
              <img 
                src="https://customer-assets.emergentagent.com/job_fahrzeug-pruefung/artifacts/h6oma009_winvalue-logo.png" 
                alt="winvalue - Restwertermittlung"
                className="h-20 w-auto object-contain"
              />
            </div>

            <div className="flex items-center justify-center p-6 bg-white rounded-xl hover:shadow-lg transition-shadow border border-gray-100 h-32">
              <img 
                src="https://customer-assets.emergentagent.com/job_fahrzeug-pruefung/artifacts/9yg2731s_zentralruf-der-autoversicherer.jpg" 
                alt="Zentralruf der Autoversicherer"
                className="h-20 w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-forest-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Benötigen Sie ein professionelles Gutachten?
          </h2>
          <p className="text-lg text-forest-100 mb-8">
            Kontaktieren Sie uns noch heute. Wir beraten Sie kostenlos und unverbindlich.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/kontakt">
              <Button size="lg" className="bg-white text-forest-700 hover:bg-gray-100 w-full sm:w-auto">
                Jetzt Kontakt aufnehmen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-forest-600 w-full sm:w-auto">
                Anrufen: {companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
